import {t} from 'i18next';
import {useMemo, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {setClientId} from '../../redux/slices/savedSearchesTemplateSlice';
import {useDispatch} from 'react-redux';
import {PAGE_NAME, usePermissionsByPage} from './usePermissionsByPage';

export enum PAGE_TYPE {
  Home = 'eca-home',
  Index = 'manage-index',
  Overlay = 'manage-overlay',
  Saved_Searches = 'saved-searches',
  Saved_Search_Templates = 'saved-search-templates',
  Export = 'export',
  Field = 'field',
}

const useManageMenuByPermissions = (
  page_type: PAGE_TYPE,
  clientId?: string
) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const manageIndexesRef = useRef({
    type: PAGE_TYPE.Index,
    label: t('fieldManagement.manageMenu.manageIndexes'),
    onClick: () => {
      navigate('/indexmanagement');
    },
  });

  const manageOverlayRef = useRef({
    type: PAGE_TYPE.Overlay,
    label: t('fieldManagement.manageMenu.manageOverlay'),
    onClick: () => {
      navigate('/eci-home-page/manage-overlays');
    },
  });

  const manageSavedSearchesRef = useRef({
    type: PAGE_TYPE.Saved_Searches,
    label: t('fieldManagement.manageMenu.manageSavedSearches'),
    onClick: () => {
      navigate('/eci-home-page/saved-search');
    },
  });

  const manageSavedSearchesTemplateRef = useRef({
    type: PAGE_TYPE.Saved_Search_Templates,
    label: t('fieldManagement.manageMenu.manageSavedSearchesTemplate'),
    onClick: () => {
      clientId && dispatch(setClientId(clientId));
      navigate('/clients/saved-searches-template', {state: {from: 'core_eci'}});
    },
  });

  const manageExportsRef = useRef({
    type: PAGE_TYPE.Export,
    label: t('fieldManagement.manageMenu.manageExports'),
    onClick: () => {
      navigate('/eci-home-page/export-management');
    },
  });

  const {
    hasECIIndexingPerm,
    hasOverlayPerm,
    hasSavedSearchesPerm,
    hasSavedSearchTemplatesPerm,
    hasExportPerm,
    hasManageSavedSearchesPerm,
  } = usePermissionsByPage(PAGE_NAME.MANAGE_MENU);

  const manageFieldRef = useRef({
    type: PAGE_TYPE.Field,
    label: t('fieldManagement.manageFields'),
    onClick: () => {
      navigate('/field-management');
    },
  });

  const manageMenus = useMemo(() => {
    switch (page_type) {
      case PAGE_TYPE.Home: {
        const menus = [
          ...(hasECIIndexingPerm ? [manageIndexesRef.current] : []),
          ...(hasOverlayPerm ? [manageOverlayRef.current] : []),
          ...[manageFieldRef.current],
          ...(hasSavedSearchesPerm && hasManageSavedSearchesPerm
            ? [manageSavedSearchesRef.current]
            : []),
          ...(hasSavedSearchTemplatesPerm
            ? [manageSavedSearchesTemplateRef.current]
            : []),
          ...(hasExportPerm ? [manageExportsRef.current] : []),
        ];
        return menus;
      }
      case PAGE_TYPE.Index:
      case PAGE_TYPE.Overlay:
      case PAGE_TYPE.Field:
      case PAGE_TYPE.Saved_Searches:
      case PAGE_TYPE.Export:
        return [
          ...(hasECIIndexingPerm ? [manageIndexesRef.current] : []),
          ...(hasOverlayPerm ? [manageOverlayRef.current] : []),
          ...[manageFieldRef.current],
          ...(hasSavedSearchesPerm && hasManageSavedSearchesPerm
            ? [manageSavedSearchesRef.current]
            : []),
          ...(hasExportPerm ? [manageExportsRef.current] : []),
        ].filter((item) => item.type !== page_type);
      case PAGE_TYPE.Saved_Search_Templates:
        return [];
    }
  }, [
    hasECIIndexingPerm,
    hasExportPerm,
    hasManageSavedSearchesPerm,
    hasOverlayPerm,
    hasSavedSearchTemplatesPerm,
    hasSavedSearchesPerm,
    page_type,
  ]);

  return {manageMenus};
};

export {useManageMenuByPermissions};
